<template>
  <div ref="canvasContainer" class="canvas-container"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

/**
 * ParticleStar Vue Component
 *
 * Features:
 * - Central independent glowing star (Core)
 * - Surrounding accretion disk (Halo)
 * - Halo touches the star (Radius Min = Core Radius)
 * - Internal Halo tilt: -45 deg Z, 45 deg X
 * - Galaxy Structure tilt: 45 deg Z, 30 deg X (Left-Front orientation)
 * - System Global tilt: +45 deg X (Front-Down adjustment)
 * - System Global tilt: -30 deg Z (Left High, Right Low adjustment)
 */

const canvasContainer = ref<HTMLDivElement | null>(null)

// 移动端检测
const isMobile =
  typeof window !== 'undefined' &&
  (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  ) ||
    window.innerWidth < 768)

// Three.js variables
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer
let controls: OrbitControls
let animationFrameId: number

// Objects
let systemGroup: THREE.Group // Outer pivot
let galaxyGroup: THREE.Group // Inner structure
let coreMesh: THREE.Points
let haloMesh: THREE.Points
let haloGroup: THREE.Group

// Config - 移动端适度减少粒子数量
const CONFIG = {
  coreCount: isMobile ? 8000 : 15000,
  haloCount: isMobile ? 20000 : 35000,
  coreRadius: 12,
  haloRadiusMin: 12, // Matches coreRadius so they touch
  haloRadiusMax: 85,
  // Internal Halo Tilt
  haloTiltZ: -45,
  haloTiltX: 45,
  // Galaxy Structure Tilt
  galaxyTiltZ: 45,
  galaxyTiltX: 30,
  // Global System Adjustment
  systemTiltX: 25, // Front-Down
  systemTiltZ: -20, // Left High, Right Low
  // 移动端性能配置
  maxPixelRatio: isMobile ? 2 : window.devicePixelRatio,
}

onMounted(() => {
  initThree()
  createSceneHierarchy() // Setup groups
  createCore() // Add core to galaxy
  createHalo() // Add halo to galaxy
  animate()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  cancelAnimationFrame(animationFrameId)
  if (renderer) renderer.dispose()
  if (controls) controls.dispose()
})

const initThree = () => {
  if (!canvasContainer.value) return

  // Scene
  scene = new THREE.Scene()
  scene.fog = new THREE.FogExp2(0x020202, 0.003) // Deep space fog

  // Camera
  const width = canvasContainer.value.clientWidth
  const height = canvasContainer.value.clientHeight
  camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000)
  // 移动端相机距离更远，让恒星显示得更小
  camera.position.set(0, 0, isMobile ? 40 : 30)

  // Renderer
  renderer = new THREE.WebGLRenderer({
    antialias: !isMobile,
    alpha: true,
    powerPreference: 'high-performance',
  })
  renderer.setSize(width, height)
  renderer.setPixelRatio(
    Math.min(window.devicePixelRatio, CONFIG.maxPixelRatio),
  )
  renderer.setClearColor(0x000000, 1)

  canvasContainer.value.appendChild(renderer.domElement)

  // Controls
  controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.05
  controls.enableZoom = false // 控制缩放
}

// --- Texture Generator ---
const createParticleTexture = () => {
  const canvas = document.createElement('canvas')
  canvas.width = 32
  canvas.height = 32
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16)
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)')
    grad.addColorStop(0.2, 'rgba(255, 255, 255, 0.8)')
    grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)')
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, 32, 32)
  }
  return new THREE.CanvasTexture(canvas)
}

// --- Create Group Hierarchy ---
const createSceneHierarchy = () => {
  // 1. System Group: Applies the new "Front-Down" and "Left High" adjustments
  systemGroup = new THREE.Group()
  systemGroup.rotation.x = THREE.MathUtils.degToRad(CONFIG.systemTiltX)
  systemGroup.rotation.z = THREE.MathUtils.degToRad(CONFIG.systemTiltZ)
  scene.add(systemGroup)

  // 2. Galaxy Group: Holds the "Left-Front" oriented galaxy
  galaxyGroup = new THREE.Group()
  galaxyGroup.rotation.z = THREE.MathUtils.degToRad(CONFIG.galaxyTiltZ)
  galaxyGroup.rotation.x = THREE.MathUtils.degToRad(CONFIG.galaxyTiltX)

  systemGroup.add(galaxyGroup)
}

// 恒星颜色配置 - 从外到内渐变
const CORE_COLORS = [
  new THREE.Color('#8ACBB2'), // 最内层
  new THREE.Color('#47B897'),
  new THREE.Color('#37BEEF'),
  new THREE.Color('#004296'), // 最外层
]

// 根据半径比例获取渐变颜色
const getGradientColor = (ratio: number): THREE.Color => {
  const segmentCount = CORE_COLORS.length - 1
  const segment = Math.min(Math.floor(ratio * segmentCount), segmentCount - 1)
  const localRatio = ratio * segmentCount - segment

  const tempColor = new THREE.Color()
  tempColor.lerpColors(
    CORE_COLORS[segment],
    CORE_COLORS[segment + 1],
    localRatio,
  )
  return tempColor
}

// --- Core Creation ---
const createCore = () => {
  const geometry = new THREE.BufferGeometry()
  const positions = new Float32Array(CONFIG.coreCount * 3)
  const colors = new Float32Array(CONFIG.coreCount * 3)
  const sizes = new Float32Array(CONFIG.coreCount)

  for (let i = 0; i < CONFIG.coreCount; i++) {
    const i3 = i * 3
    // Sphere distribution
    const r = Math.pow(Math.random(), 1 / 3) * CONFIG.coreRadius
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)

    positions[i3] = r * Math.sin(phi) * Math.cos(theta)
    positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta)
    positions[i3 + 2] = r * Math.cos(phi)

    // 根据半径比例设置颜色（内层 #8ACBB2 -> 外层 #004296）
    const radiusRatio = r / CONFIG.coreRadius
    const color = getGradientColor(radiusRatio)
    colors[i3] = color.r
    colors[i3 + 1] = color.g
    colors[i3 + 2] = color.b

    sizes[i] = Math.random() * 0.8 + 0.2
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

  const material = new THREE.PointsMaterial({
    size: 0.6,
    vertexColors: true,
    map: createParticleTexture(),
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    transparent: true,
    opacity: 0.9,
  })

  coreMesh = new THREE.Points(geometry, material)

  // Add to galaxy group (inner)
  if (galaxyGroup) galaxyGroup.add(coreMesh)
  else scene.add(coreMesh)
}

/**
 * 光晕渐变色设置位置
 * =====================
 * 光晕颜色在 createHalo() 函数中设置，使用 getGradientColor(1 - radiusRatio) 实现从外到内渐变
 * 颜色配置在 CORE_COLORS 数组中定义：
 * - 外层: #004296
 * - 中间: #37BEEF -> #47B897
 * - 内层: #8ACBB2
 */

// --- Halo Creation ---
const createHalo = () => {
  const geometry = new THREE.BufferGeometry()
  const positions = new Float32Array(CONFIG.haloCount * 3)
  const colors = new Float32Array(CONFIG.haloCount * 3)
  const sizes = new Float32Array(CONFIG.haloCount)

  for (let i = 0; i < CONFIG.haloCount; i++) {
    const i3 = i * 3

    // Ring/Disk distribution
    const rBase = Math.random()
    // Distribute from Min (Core Edge) to Max
    const r =
      CONFIG.haloRadiusMin +
      Math.pow(rBase, 1.4) * (CONFIG.haloRadiusMax - CONFIG.haloRadiusMin)

    const angle = Math.random() * Math.PI * 2

    // Create on XY Plane (Flat disk facing camera)
    const z = (Math.random() - 0.5) * 1.0

    // Optional: Add slight spiral offset
    const spiralOffset = r * 0.05
    const finalX = r * Math.cos(angle + spiralOffset)
    const finalY = r * Math.sin(angle + spiralOffset)

    positions[i3] = finalX
    positions[i3 + 1] = finalY
    positions[i3 + 2] = z

    // Color Gradient - 从外到内渐变（外层 #004296 -> 内层 #8ACBB2）
    const radiusRatio =
      (r - CONFIG.haloRadiusMin) / (CONFIG.haloRadiusMax - CONFIG.haloRadiusMin)
    const color = getGradientColor(1 - radiusRatio)

    colors[i3] = color.r
    colors[i3 + 1] = color.g
    colors[i3 + 2] = color.b

    sizes[i] = Math.random() * 0.6 + 0.1
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

  const material = new THREE.PointsMaterial({
    size: 0.5,
    vertexColors: true,
    map: createParticleTexture(),
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    transparent: true,
    opacity: 0.6,
  })

  haloMesh = new THREE.Points(geometry, material)

  // Create a Group to hold the Halo and apply the Tilt
  haloGroup = new THREE.Group()
  haloGroup.add(haloMesh)

  // --- APPLY INTERNAL TILT ---
  haloGroup.rotation.z = THREE.MathUtils.degToRad(CONFIG.haloTiltZ)
  haloGroup.rotation.x = THREE.MathUtils.degToRad(CONFIG.haloTiltX)

  if (galaxyGroup) galaxyGroup.add(haloGroup)
  else scene.add(haloGroup)
}

// --- Animation ---
const animate = () => {
  // 1. Rotate Star Core (Vertical Axis) - 恒星旋转速度
  if (coreMesh) {
    coreMesh.rotation.y += 0.001
  }

  // 2. Rotate Halo (Spinning around the star within the tilted plane) - 光晕旋转速度
  if (haloMesh) {
    haloMesh.rotation.z -= 0.0008
  }

  controls.update()
  renderer.render(scene, camera)
  animationFrameId = requestAnimationFrame(animate)
}

const handleResize = () => {
  if (!canvasContainer.value) return
  const width = canvasContainer.value.clientWidth
  const height = canvasContainer.value.clientHeight

  camera.aspect = width / height
  camera.updateProjectionMatrix()
  renderer.setSize(width, height)
}
</script>

<style scoped>
.canvas-container {
  width: 100%;
  height: 100%;
  background-color: #000;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  touch-action: pan-y pinch-zoom;
}

@media (max-width: 768px) {
  .canvas-container {
    position: relative;
    min-height: 250px;
  }
}
</style>
